import type { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react/macro'
import {
  Field,
  FieldDescription,
  FieldLabel,
} from 'designSystem/components/Field'
import {
  ToggleGroup,
  ToggleGroupItem,
} from 'designSystem/components/ToggleGroup'
import {
  type BugReportPriority,
  bugReportPrioritySchema,
} from 'features/knowledgeDocumentViewer/utils/viewerBugReport'
import { useCallback } from 'react'

const PRIORITY_HELP: Record<BugReportPriority, MessageDescriptor> = {
  high: msg`Solve now, including outside working hours.`,
  medium: msg`First thing during working hours.`,
  low: msg`Minor inconvenience — prioritize accordingly.`,
}

type BugReportPriorityFieldProps = {
  value: BugReportPriority
  disabled?: boolean
  onChange: (priority: BugReportPriority) => void
}

export function BugReportPriorityField({
  value,
  disabled = false,
  onChange,
}: BugReportPriorityFieldProps) {
  const { t, i18n } = useLingui()
  const handleChange = useCallback(
    (nextValue: string) => {
      const parsed = bugReportPrioritySchema.safeParse(nextValue)
      if (parsed.success) {
        onChange(parsed.data)
      }
    },
    [onChange]
  )

  return (
    <Field>
      <FieldLabel htmlFor='bug-report-priority'>{t`Priority`}</FieldLabel>
      <ToggleGroup
        id='bug-report-priority'
        type='single'
        variant='outline'
        size='lg'
        value={value}
        disabled={disabled}
        onValueChange={handleChange}>
        <ToggleGroupItem value='high'>{t`High`}</ToggleGroupItem>
        <ToggleGroupItem value='medium'>{t`Med`}</ToggleGroupItem>
        <ToggleGroupItem value='low'>{t`Low`}</ToggleGroupItem>
      </ToggleGroup>
      <FieldDescription>{i18n._(PRIORITY_HELP[value])}</FieldDescription>
    </Field>
  )
}
